import { DicewarePassphrase } from './diceware-passphrase.js';
import { fromHex, toHex } from './util.js';

const saltLength = 16; // 16 bytes for AES-GCM

export class SecureContentsId {
  private _dek: CryptoKey | null = null;

  constructor(readonly id: string) {}

  private get idEndPosition(): number {
    return this.id.length - saltLength * 2; // 2 hex characters per byte
  }

  get key(): string {
    return this.id.slice(0, this.idEndPosition);
  }

  get readableKey(): string {
    return new TextDecoder().decode(fromHex(this.key));
  }

  get salt(): string {
    return this.id.slice(this.idEndPosition, this.id.length);
  }

  async getIdHash() {
    const hash = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(this.id),
    );
    return toHex(new Uint8Array(hash));
  }

  async getDEK(): Promise<CryptoKey> {
    if (this._dek) {
      return this._dek;
    }

    const saltHex = this.salt;
    if (saltHex.length !== 32) {
      throw new Error('SecureContentsId salt must be 32 characters long');
    }

    const keyHex = this.key;

    const encoder = new TextEncoder();
    const keyMaterial = encoder.encode(keyHex);

    const baseKey = await crypto.subtle.importKey(
      'raw',
      keyMaterial,
      { name: 'PBKDF2' },
      false,
      ['deriveBits', 'deriveKey']
    );
    if (!baseKey || !(baseKey instanceof CryptoKey)) {
      throw new Error('Failed to derive key from token');
    }

    const salt = fromHex(saltHex);

    const derivedKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      baseKey,
      {
        name: 'AES-GCM',
        length: 256, // 256 bits
      },
      true, // Extractable
      ['encrypt', 'decrypt']
    );
    this._dek = derivedKey;
    if (!this._dek || !(this._dek instanceof CryptoKey)) {
      throw new Error('Failed to derive DEK from token');
    }
    return this._dek;
  }


  static async fromRandomBytes(wordList: string[]) {
    const passphrase = await DicewarePassphrase.fromRandom(wordList).then(x => x.toString());

    const salt = toHex(
      crypto.getRandomValues(new Uint8Array(16))
    );

    return SecureContentsId.fromParts(passphrase, salt);
  }

  static fromParts(passphrase: string, salt: string) {
    const key = toHex(
      new TextEncoder().encode(passphrase)
    );

    const id = new SecureContentsId(`${key}${salt}`);
    return id;
  }
}
